import LayerPricing from "./LayerPricing";
import {
  Box,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from "@mui/material";
import Component from "components/Component";
import Disabled from "components/common/Disabled";
import FeatureFlagged from "components/common/FeatureFlagged";
import PeerReviewNotes from "components/common/PeerReviewNotes";
import PrettyNumberTextField from "components/common/PrettyNumberTextField";
import TabbedTextNote from "components/common/TabbedTextNote";
import TextNote from "components/common/TextNote";
import Visible from "components/common/Visible";
import SpecialAcceptance from "components/pricing/specialAcceptance/SpecialAcceptance";
import * as config from "config";
import { PRICING_ENABLE_SPLIT_UW_LOAD } from "flags";
import { snakeCase } from "lodash";
import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useConfigQuery, useFeatureFlag } from "services/staticDataService";
import * as programActions from "store/actions/input/program/programActions";
import * as programPricingStore from "store/programPricing";
import * as programSelectors from "store/selectors/input/program/programSelectors";
import { selectLayers } from "store/selectors/pricing/pricingSelectors";

const PricingLoadField = ({ label, name }) => {
  const dispatch = useDispatch();

  const programPricing = useSelector(programPricingStore.select);

  return (
    <Box sx={{ flex: 1 }}>
      <Disabled ifReadOnly>
        <PrettyNumberTextField
          suffix={"%"}
          InputLabelProps={{ shrink: true }}
          label={label}
          value={programPricing?.pricingLoads?.[name] ?? null}
          onChangeNumber={(value) =>
            dispatch(
              programPricingStore.update({
                pricingLoads: {
                  ...programPricing?.pricingLoads,
                  [name]: value,
                },
              })
            )
          }
          variant={"filled"}
        />
      </Disabled>
    </Box>
  );
};

const Inputs = () => {
  const dispatch = useDispatch();

  const programPricing = useSelector(programPricingStore.select);
  const resolvedProgram = useSelector(programSelectors.selectResolvedProgram);

  const updateProgramPricing = (values) => {
    dispatch(programPricingStore.update(values));
  };

  const updateBrokerage = (value) => {
    dispatch(programActions.updateBrokerage(value));
  };

  const updateExpenses = (value) => {
    dispatch(programActions.updateExpenses(value));
  };

  const getPricingValue = (name) => {
    return programPricing?.pricingLoads?.[name];
  };
  const setPricingValue = (name, value) => {
    updateProgramPricing({
      pricingLoads: {
        ...programPricing.pricingLoads,
        [name]: value,
      },
    });
  };

  const splitLoadEnabled =
    useFeatureFlag(PRICING_ENABLE_SPLIT_UW_LOAD, {
      default: false,
    }) || !!getPricingValue("SPLIT_UW_LOAD");

  return (
    <>
      <Component
        md_width={6}
        title={"Pricing Loads"}
        options={
          <>
            {splitLoadEnabled && (
              <FormControlLabel
                label={
                  <Typography variant={"subtitle2"}>
                    {"Split cat/non-cat"}
                  </Typography>
                }
                labelPlacement={"start"}
                control={
                  <Switch
                    size={"small"}
                    checked={!!getPricingValue("SPLIT_UW_LOAD")}
                    onChange={(e) =>
                      setPricingValue("SPLIT_UW_LOAD", e.target.checked)
                    }
                  />
                }
              />
            )}
          </>
        }
      >
        <Stack direction={"row"} spacing={config.GRID_SPACING}>
          <PricingLoadField label={"ALAE"} name={"ALAE"} />
          <PricingLoadField label={"ITV"} name={"ITV"} />
          <Visible
            byTag={"<DnfLayerPricing>.pricingLoads.growth"}
            defaultVisible
          >
            <PricingLoadField label={"Growth"} name={"GROWTH"} />
          </Visible>
          {splitLoadEnabled && !!getPricingValue("SPLIT_UW_LOAD") ? (
            <>
              <PricingLoadField label={"UW Cat Load"} name={"UW_LOAD_CAT"} />
              <PricingLoadField
                label={"UW Non Cat Load"}
                name={"UW_LOAD_NON_CAT"}
              />
            </>
          ) : (
            <PricingLoadField label={"UW Load"} name={"UW_LOAD"} />
          )}
        </Stack>
      </Component>
      <Component title={"Expense Loads"} md_width={6}>
        <Grid container spacing={config.GRID_SPACING}>
          <Grid item xs={3}>
            <Disabled ifReadOnly>
              <PrettyNumberTextField
                suffix={"%"}
                InputLabelProps={{ shrink: true }}
                label={"Brokerage"}
                value={resolvedProgram?.brokerage ?? 0}
                onChangeNumber={updateBrokerage}
                variant={"filled"}
              />
            </Disabled>
          </Grid>
          <Grid item xs={3}>
            <Disabled ifReadOnly>
              <PrettyNumberTextField
                suffix={"%"}
                InputLabelProps={{ shrink: true }}
                label={"Helix Fee"}
                value={programPricing?.expenseLoads?.HELIX_FEE}
                onChangeNumber={(value) =>
                  updateProgramPricing({
                    expenseLoads: {
                      ...programPricing.expenseLoads,
                      HELIX_FEE: value,
                    },
                  })
                }
                variant={"filled"}
              />
            </Disabled>
          </Grid>
          <Grid item xs={3}>
            <Disabled ifReadOnly>
              <PrettyNumberTextField
                suffix={"%"}
                InputLabelProps={{ shrink: true }}
                label={"General Expense"}
                value={resolvedProgram?.expenses ?? 0}
                onChangeNumber={updateExpenses}
                variant={"filled"}
              />
            </Disabled>
          </Grid>
        </Grid>
      </Component>
    </>
  );
};

const AdditionalInputs = () => {
  const dispatch = useDispatch();

  const programPricing = useSelector(programPricingStore.select);

  const updateProgramPricing = (values) => {
    dispatch(programPricingStore.update(values));
  };

  return (
    <>
      <Component title={"Expected Loss Ratios"} xs_width={6}>
        <Grid container spacing={config.GRID_SPACING}>
          <Grid item xs={6}>
            <Disabled ifReadOnly>
              <PrettyNumberTextField
                suffix={"%"}
                InputLabelProps={{ shrink: true }}
                label={"Cat ELR"}
                value={programPricing?.CAT_ELR}
                onChangeNumber={(CAT_ELR) => updateProgramPricing({ CAT_ELR })}
                variant={"filled"}
              />
            </Disabled>
          </Grid>
          <Grid item xs={6}>
            <Disabled ifReadOnly>
              <PrettyNumberTextField
                suffix={"%"}
                InputLabelProps={{ shrink: true }}
                label={"Non-Cat ELR"}
                value={programPricing?.non_CAT_ELR}
                onChangeNumber={(non_CAT_ELR) =>
                  updateProgramPricing({ non_CAT_ELR })
                }
                variant={"filled"}
              />
            </Disabled>
          </Grid>
        </Grid>
      </Component>
      <Component title={"Scales"} xs_width={6}>
        <Grid container spacing={config.GRID_SPACING}>
          <Grid item xs={6}>
            <Disabled ifReadOnly>
              <FormControl variant={"filled"} fullWidth>
                <InputLabel shrink={true}>{"Cat Scale"}</InputLabel>
                <Select
                  value={programPricing?.catScale ?? "__NULL__"}
                  onChange={(e) => {
                    let catScale = e.target.value;
                    if (catScale === "__NULL__") {
                      catScale = null;
                    }
                    updateProgramPricing({ catScale });
                  }}
                >
                  <MenuItem value={"__NULL__"}>{"---"}</MenuItem>
                  <MenuItem value={"C"}>{"C Scale"}</MenuItem>
                  <MenuItem value={"MODEL"}>{"Modelling"}</MenuItem>
                  <MenuItem value={"CAT"}>{"Cat Scale"}</MenuItem>
                  <Divider />
                  <MenuItem value={"LDN_Low"}>{"LDN Low"}</MenuItem>
                  <MenuItem value={"LDN_Normal"}>{"LDN Normal"}</MenuItem>
                  <MenuItem value={"LDN_High"}>{"LDN High"}</MenuItem>
                  <MenuItem value={"LDN_Excess"}>{"LDN Excess"}</MenuItem>
                </Select>
              </FormControl>
            </Disabled>
          </Grid>
          <Grid item xs={6}>
            <Disabled ifReadOnly>
              <FormControl variant={"filled"} fullWidth>
                <InputLabel shrink={true}>{"Non-Cat Scale"}</InputLabel>
                <Select
                  value={programPricing?.nonCatScale ?? "__NULL__"}
                  onChange={(e) => {
                    let nonCatScale = e.target.value;
                    if (nonCatScale === "__NULL__") {
                      nonCatScale = null;
                    }
                    updateProgramPricing({ nonCatScale });
                  }}
                >
                  <MenuItem value={"__NULL__"}>{"---"}</MenuItem>
                  <MenuItem value={"A"}>{"A Scale"}</MenuItem>
                  <MenuItem value={"B"}>{"B Scale"}</MenuItem>
                  <MenuItem value={"C"}>{"C Scale"}</MenuItem>
                  <MenuItem value={"D"}>{"D Scale"}</MenuItem>
                  <MenuItem value={"N"}>{"N Scale"}</MenuItem>
                  <MenuItem value={"LDN_Low"}>{"LDN Low"}</MenuItem>
                  <MenuItem value={"LDN_Normal"}>{"LDN Normal"}</MenuItem>
                  <MenuItem value={"LDN_High"}>{"LDN High"}</MenuItem>
                  <MenuItem value={"LDN_Excess"}>{"LDN Excess"}</MenuItem>
                </Select>
              </FormControl>
            </Disabled>
          </Grid>
        </Grid>
      </Component>
    </>
  );
};

const DnfLayerPricing = () => {
  const [showAdditionalInputs, setShowAdditionalInputs] = useState(false);
  const layerData = useSelector(selectLayers);
  const { data: policyFormsConfig } = useConfigQuery("policy_forms");

  const tabData = useMemo(() => {
    const policyFormMetadata = policyFormsConfig?.policy_forms ?? [];

    // Build map: group → Set of codes
    const groupToCodes = policyFormMetadata.reduce(
      (map, { group, code }) =>
        group && code
          ? map.set(group, (map.get(group) ?? new Set()).add(code))
          : map,
      new Map()
    );

    // use group_order when present in policy_forms config,
    // otherwise use the group keys in the order they first appear in policy_forms config
    const groupOrder = policyFormsConfig?.group_order ?? [
      ...groupToCodes.keys(),
    ];

    // Active codes from layers
    const activeCodes = new Set(
      layerData.map(({ policyForm }) => policyForm).filter(Boolean)
    );

    // Iterate groupOrder, include group if any of its codes are active
    return groupOrder
      .filter((group) =>
        [...groupToCodes.get(group)]?.some((code) => activeCodes.has(code))
      )
      .map((group) => ({
        key: `policyForm-${snakeCase(group)}`,
        label: group,
      }));
  }, [layerData, policyFormsConfig]);

  const emptyTabContent = (
    <Typography variant={"body1"} color={"text.secondary"}>
      {"No policy form currently selected."}
    </Typography>
  );

  return (
    <>
      <Visible byTag={"<DnfLayerPricing>.inputs"} defaultVisible>
        <Inputs />
      </Visible>
      <Visible byTag={"<DnfLayerPricing>.additionalInputs"} defaultVisible>
        {showAdditionalInputs && <AdditionalInputs />}
      </Visible>
      <>
        <Grid item xs={12} lg={6}>
          <LayerPricing
            showAdditionalInputs={showAdditionalInputs}
            setShowAdditionalInputs={setShowAdditionalInputs}
          />
        </Grid>
        <Grid item xs={12} lg={6}>
          <Stack
            direction={"column"}
            spacing={config.GRID_SPACING}
            justifyContent={"flex-start"}
          >
            <SpecialAcceptance />
            <FeatureFlagged
              flag={"ui/tabbedTextNote"}
              on={() => (
                <TabbedTextNote
                  noteKey={"summary"}
                  tabData={tabData}
                  title={"Notes"}
                  emptyTabContent={emptyTabContent}
                />
              )}
              off={() => <TextNote noteKey={"summary"} title={"Notes"} />}
            />
            <TextNote noteKey={"program"} title={"Risk/Program Notes"} />
            <TextNote noteKey={"riskControl"} title={"Risk Control Comments"} />
            <TextNote noteKey={"claims"} title={"Loss History"} />
            <TextNote noteKey={"modeling"} title={"Modeling Notes"} />
            <Disabled ifReadOnly>
              <PeerReviewNotes
                title={"Peer Review"}
                lg_width={12}
                growVertically={false}
              />
            </Disabled>
          </Stack>
        </Grid>
      </>
    </>
  );
};

export default DnfLayerPricing;
